import { useCallback, useState } from 'react'
import type { ChangeEvent, FormEvent } from 'react'
import type { Location, NavigateFunction } from 'react-router-dom'
import type { StatusMessage } from '../types/status'
import type {
  BootstrapPayload,
  ForgotPasswordPayload,
  LoginCredentials,
  LoginResponse,
  ProfileFormState,
  ResetPasswordPayload
} from '../types/auth'
import type { AuthUserRole } from '../types/user'
import type { Permission } from '../types/user'
import { hasPermission as checkPermission } from '../utils/permissions'
import { clearLegacyAuthStorage, normalizeAuthUserData } from './auth/shared'
import { useAuthProfileForm } from './auth/useAuthProfileForm'
import { useAuthSessionActions } from './auth/useAuthSessionActions'
import { useAuthSessionBootstrap } from './auth/useAuthSessionBootstrap'
import { usePasswordResetFlow } from './auth/usePasswordResetFlow'

export interface UseAuthParams {
  apiBaseUrl: string
  navigate: NavigateFunction
  location: Location
}

export interface UseAuthResult {
  authStatus: StatusMessage | null
  setAuthStatus: (status: StatusMessage | null) => void
  setupRequired: boolean
  isAuthMetaLoaded: boolean
  isSessionCheckFailed: boolean
  sessionCheckErrorMessage: string | null
  isSessionCheckRetrying: boolean
  retrySessionCheck: () => void
  isAuthenticated: boolean
  signedOutByUser: boolean
  isAuthLoading: boolean
  authUserId: number
  authUser: string
  authUsername: string
  authRole: AuthUserRole
  authPermissions: Permission[]
  hasPermission: (permission: Permission) => boolean
  isAdmin: boolean
  requirePasswordChange: boolean
  isResetPasswordFlow: boolean
  initialResetToken: string
  legacyResetUrl: string
  profileForm: ProfileFormState
  profileStatus: StatusMessage | null
  isProfileSubmitting: boolean
  openProfilePage: () => void
  handleUnauthorizedSession: () => void
  handleProfileFormChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleProfileSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>
  handleLogin: (credentials: LoginCredentials) => Promise<void>
  handleForgotPassword: (payload: ForgotPasswordPayload) => Promise<void>
  handleResetPassword: (payload: ResetPasswordPayload) => Promise<void>
  handleBootstrap: (payload: BootstrapPayload) => Promise<void>
  handleLogout: () => Promise<void>
}

export function useAuth({ apiBaseUrl, navigate, location }: UseAuthParams): UseAuthResult {
  const [authStatus, setAuthStatus] = useState<StatusMessage | null>(null)
  const [setupRequired, setSetupRequired] = useState(false)
  const [isAuthMetaLoaded, setIsAuthMetaLoaded] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [signedOutByUser, setSignedOutByUser] = useState(false)
  const [authUserId, setAuthUserId] = useState(0)
  const [authUser, setAuthUser] = useState('')
  const [authUsername, setAuthUsername] = useState('')
  const [authEmail, setAuthEmail] = useState('')
  const [authRole, setAuthRole] = useState<AuthUserRole>('User')
  const [authPermissions, setAuthPermissions] = useState<Permission[]>([])
  const [requirePasswordChange, setRequirePasswordChange] = useState(false)

  const isAuthLoading = authStatus?.kind === 'loading'
  const hasPermission = useCallback((permission: Permission): boolean => checkPermission(authPermissions, permission), [authPermissions])
  const isAdmin = hasPermission('SystemAdmin')

  const clearSessionState = useCallback((): void => {
    clearLegacyAuthStorage()
    setIsAuthenticated(false)
    setAuthUserId(0)
    setAuthUser('')
    setAuthUsername('')
    setAuthEmail('')
    setAuthRole('User')
    setAuthPermissions([])
    setRequirePasswordChange(false)
  }, [])

  const applyAuthUserData = useCallback((userData: Partial<LoginResponse>, fallbackUserName = ''): {
    userName: string
    userEmail: string
    mustChangePassword: boolean
  } => {
    const {
      userId,
      userName,
      username,
      userEmail,
      userRole,
      userPermissions,
      mustChangePassword
    } = normalizeAuthUserData(userData, fallbackUserName)

    setIsAuthenticated(userId > 0 || username.length > 0)
    setSignedOutByUser(false)
    setAuthUserId(userId)
    setAuthUser(userName)
    setAuthUsername(username)
    setAuthEmail(userEmail)
    setAuthRole(userRole)
    setAuthPermissions(userPermissions)
    setRequirePasswordChange(mustChangePassword)

    return { userName, userEmail, mustChangePassword }
  }, [])

  const {
    profileForm,
    profileStatus,
    isProfileSubmitting,
    setProfileStatus,
    clearProfileState,
    resetProfileForm,
    openProfilePage,
    handleProfileFormChange,
    handleProfileSubmit
  } = useAuthProfileForm({
    apiBaseUrl,
    navigate,
    isAuthenticated,
    authUser,
    authEmail,
    requirePasswordChange,
    clearSessionState,
    setAuthStatus,
    setAuthUser,
    setAuthEmail,
    setRequirePasswordChange
  })

  const clearAuthState = useCallback((): void => {
    clearSessionState()
    clearProfileState()
  }, [clearProfileState, clearSessionState])

  const { isResetPasswordFlow, initialResetToken, legacyResetUrl, handleForgotPassword, handleResetPassword } = usePasswordResetFlow({
    apiBaseUrl,
    navigate,
    location,
    setAuthStatus
  })

  const { handleLogin, handleBootstrap, handleLogout, handleUnauthorizedSession } = useAuthSessionActions({
    apiBaseUrl,
    navigate,
    location,
    clearAuthState,
    applyAuthUserData,
    resetProfileForm,
    setSetupRequired,
    setSignedOutByUser,
    setAuthStatus,
    setProfileStatus
  })

  const {
    isSessionCheckFailed,
    sessionCheckErrorMessage,
    isSessionCheckRetrying,
    retrySessionCheck
  } = useAuthSessionBootstrap({
    apiBaseUrl,
    navigate,
    pathname: location.pathname,
    isAuthenticated,
    isAdmin,
    clearAuthState,
    applyAuthUserData,
    resetProfileForm,
    setSetupRequired,
    setAuthStatus,
    setProfileStatus,
    setIsAuthMetaLoaded
  })

  return {
    authStatus,
    setAuthStatus,
    setupRequired,
    isAuthMetaLoaded,
    isSessionCheckFailed,
    sessionCheckErrorMessage,
    isSessionCheckRetrying,
    retrySessionCheck,
    isAuthenticated,
    signedOutByUser,
    isAuthLoading,
    authUserId,
    authUser,
    authUsername,
    authRole,
    authPermissions,
    hasPermission,
    isAdmin,
    requirePasswordChange,
    isResetPasswordFlow,
    initialResetToken,
    legacyResetUrl,
    profileForm,
    profileStatus,
    isProfileSubmitting,
    openProfilePage,
    handleUnauthorizedSession,
    handleProfileFormChange,
    handleProfileSubmit,
    handleLogin,
    handleForgotPassword,
    handleResetPassword,
    handleBootstrap,
    handleLogout
  }
}
